import React, { useState, useEffect } from 'react';
import {
  Paper,
  Grid,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { settingsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const Settings = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [masterData, setMasterData] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentTable, setCurrentTable] = useState('');
  const [formData, setFormData] = useState({ name: '', display_order: 0, is_active: true });

  useEffect(() => {
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    setLoading(true);
    try {
      const response = await settingsAPI.getMasterData();
      setMasterData(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (table, item = null) => {
    setCurrentTable(table);
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        display_order: item.display_order || 0,
        is_active: item.is_active === 1,
        ...(item.description && { description: item.description })
      });
    } else {
      setEditingItem(null);
      setFormData({ name: '', display_order: 0, is_active: true });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await settingsAPI.updateMasterData(currentTable, editingItem.id, formData);
        toast.success('Updated successfully');
      } else {
        await settingsAPI.addMasterData(currentTable, formData);
        toast.success('Added successfully');
      }
      setDialogOpen(false);
      fetchMasterData();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (table, item) => {
    if (window.confirm(`Are you sure you want to delete ${item.name}?`)) {
      try {
        await settingsAPI.deleteMasterData(table, item.id);
        toast.success('Deleted successfully');
        fetchMasterData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const renderMasterTable = (title, data, tableName) => {
    if (!data || data.length === 0) return null;
    
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{title}</Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog(tableName)}
            >
              Add
            </Button>
          </Box>
          <Grid container spacing={1}>
            {data.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Paper sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2">{item.name}</Typography>
                    {item.display_order !== undefined && (
                      <Typography variant="caption" color="textSecondary">
                        Order: {item.display_order}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => handleOpenDialog(tableName, item)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(tableName, item)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        System Settings
      </Typography>

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Profile Settings" />
          <Tab label="Religion & Community" />
          <Tab label="Education & Work" />
          <Tab label="Location" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {renderMasterTable('Marital Status', masterData.maritalStatus, 'marital_status')}
          {renderMasterTable('Gotra', masterData.gotras, 'gotra')}
          {renderMasterTable('Heights', masterData.heights, 'heights')}
          {renderMasterTable('Weights', masterData.weights, 'weights')}
          {renderMasterTable('Diets', masterData.diets, 'diets')}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {renderMasterTable('Religions', masterData.religions, 'religions')}
          {renderMasterTable('Communities', masterData.communities, 'communities')}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {renderMasterTable('Qualification Categories', masterData.qualificationCategories, 'qualification_categories')}
          {renderMasterTable('Qualifications', masterData.qualifications, 'qualifications')}
          {renderMasterTable('Working With Categories', masterData.workingWithCategories, 'working_with_categories')}
          {renderMasterTable('Working With', masterData.workingWith, 'working_with')}
          {renderMasterTable('Working As Categories', masterData.workingAsCategories, 'working_as_categories')}
          {renderMasterTable('Working As', masterData.workingAs, 'working_as')}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {renderMasterTable('Countries', masterData.countries, 'countries')}
          {renderMasterTable('States', masterData.states, 'states')}
          {renderMasterTable('Cities', masterData.cities, 'cities')}
        </TabPanel>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit' : 'Add'} {currentTable.replace('_', ' ')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Display Order"
            type="number"
            value={formData.display_order}
            onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
            margin="normal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            }
            label="Active"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;